import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, tasksApi, clientsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Building2, CheckSquare, Users, TrendingUp, AlertCircle, Calendar,
  ArrowRight, Clock, Sparkles
} from 'lucide-react';

const FOX_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/28ykjv9m_Transparent%20BG%20Fox.png';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, tasksRes, clientsRes] = await Promise.all([
          dashboardApi.getStats(),
          tasksApi.getAll({ assignee_id: user?.id }),
          clientsApi.getAll()
        ]);
        setStats(statsRes.data);
        setRecentTasks(tasksRes.data.slice(0, 5));
        setClients(clientsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'todo': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'bg-green-100 text-green-700';
      case 'attention': return 'bg-yellow-100 text-yellow-700';
      case 'critical': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">
            {getGreeting()}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-navy/60 mt-1">Here's what's happening at CompliSun today.</p>
        </div>
        <div className="flex gap-3">
          <Button asChild className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90">
            <Link to="/tasks" data-testid="view-tasks-btn">
              <CheckSquare className="w-4 h-4 mr-2" />
              View Tasks
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-navy/10 card-hover" data-testid="stat-clients">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60 font-medium">Total Clients</p>
                <p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth'] mt-1">
                  {stats?.total_clients || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#106EFD]/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#106EFD]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-navy/10 card-hover" data-testid="stat-matters">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60 font-medium">Active Matters</p>
                <p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth'] mt-1">
                  {stats?.active_matters || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#40CFDE]/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#40CFDE]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-navy/10 card-hover" data-testid="stat-my-tasks">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60 font-medium">My Tasks</p>
                <p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth'] mt-1">
                  {stats?.my_tasks || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#37C563]/10 flex items-center justify-center">
                <CheckSquare className="w-6 h-6 text-[#37C563]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-navy/10 card-hover" data-testid="stat-overdue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-navy/60 font-medium">Overdue Tasks</p>
                <p className="text-3xl font-bold text-[#FF801D] font-['Amaranth'] mt-1">
                  {stats?.overdue_tasks || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#FF801D]/10 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-[#FF801D]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Tasks */}
        <Card className="lg:col-span-2 bg-white border-navy/10" data-testid="my-tasks-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-['Amaranth']">My Tasks</CardTitle>
            <Link to="/tasks" className="text-sm text-[#106EFD] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentTasks.length === 0 ? (
              <div className="empty-state py-8">
                <img src={FOX_MASCOT} alt="Fox mascot" className="w-24 h-24 mb-3" />
                <p className="text-navy/60">No tasks assigned yet. Time to get busy!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentTasks.map((task) => (
                  <div 
                    key={task.id} 
                    className="flex items-center gap-4 p-3 rounded-lg border border-navy/5 hover:border-navy/10 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1A2B5E] truncate">{task.title}</p>
                      {task.due_date && (
                        <p className="text-xs text-navy/50 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Badge className={getPriorityColor(task.priority)}>
                      {task.priority}
                    </Badge>
                    <Badge className={getStatusColor(task.status)}>
                      {task.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Insights */}
        <Card className="bg-white border-navy/10" data-testid="ai-insights-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Amaranth'] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#E171EA]" />
              AI Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="ai-bubble text-sm">
              <p className="font-medium mb-2">Today's Focus:</p>
              <ul className="space-y-1 text-sm">
                <li>• {stats?.overdue_tasks || 0} overdue tasks need attention</li>
                <li>• {stats?.active_matters || 0} active matters in progress</li>
                <li>• Review client health indicators</li>
              </ul>
            </div>
            <Button variant="outline" className="w-full mt-4" data-testid="ai-assistant-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Ask AI Assistant
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Clients overview */}
      <Card className="bg-white border-navy/10" data-testid="clients-overview">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-['Amaranth']">Client Overview</CardTitle>
          <Link to="/clients" className="text-sm text-[#106EFD] hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="empty-state py-8">
              <img src={FOX_MASCOT} alt="Fox mascot" className="w-24 h-24 mb-3" />
              <p className="text-navy/60">No clients yet. Add your first client!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {clients.map((client) => (
                <Link
                  key={client.id}
                  to={`/clients/${client.id}`}
                  className="p-4 rounded-lg border border-navy/10 hover:border-navy/20 hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#106EFD]/10 flex items-center justify-center mb-3">
                    <span className="text-[#106EFD] font-bold text-sm">
                      {client.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <p className="font-medium text-[#1A2B5E] truncate">{client.name}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={getHealthColor(client.health_status)} variant="outline">
                      {client.health_status}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ISM Banner */}
      <div className="bg-[#1A2B5E] rounded-xl p-6 text-white">
        <div className="flex items-center gap-4">
          <img src={FOX_MASCOT} alt="Fox" className="w-16 h-16" />
          <div>
            <p className="text-lg font-['Amaranth'] font-bold">Reminder from CompliCrew</p>
            <p className="text-white/70 mt-1">"Find Solutions, not Problems. Humor Helps. Do the Right Thing."</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
